import { useEffect, useState } from 'react';
import { useT } from '@/i18n';
import { ApiError, api } from '@/api/client';
import type { CareMember, ReportShare } from '@/api/types';
import { Alert, Button, Chip, LinkIcon, Mail, Sheet, WhatsApp, useToast } from '@/components/ui';
import { formatDate } from '@/lib/format';
import { copyText, mailtoHref, shareUrl, whatsappHref } from './share';

const DAYS = [1, 7, 30] as const;

export interface ShareLinkSheetProps {
  open: boolean;
  onClose: () => void;
  reportId: string;
  childId: string;
  childName: string;
  /** Pre-select someone from the care team. */
  memberId?: string;
}

/**
 * Makes a private link to a report's PDF that stops working after 1, 7 or 30 days, for a doctor,
 * therapist or teacher. The link is shown once; after that it can only be stopped, not copied again
 * (only its hash is stored).
 */
export const ShareLinkSheet = ({ open, onClose, reportId, childId, childName, memberId }: ShareLinkSheetProps) => {
  const t = useT();
  const toast = useToast();
  const [members, setMembers] = useState<CareMember[]>([]);
  const [shares, setShares] = useState<ReportShare[]>([]);
  const [who, setWho] = useState<string | undefined>(memberId);
  const [days, setDays] = useState<(typeof DAYS)[number]>(7);
  const [link, setLink] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!open) return;
    setLink(undefined);
    setError(undefined);
    setWho(memberId);
    void Promise.all([api.careTeam.list(childId), api.shares.list(reportId)]).then(
      ([c, s]) => {
        setMembers(c.members);
        setShares(s.shares);
      },
      () => setError(t('common.error')),
    );
  }, [open, childId, reportId, memberId, t]);

  const create = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const { share, token } = await api.shares.create({ reportId, memberId: who, days });
      setLink(shareUrl(`/shared/${token}`));
      setShares((prev) => [share, ...prev]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const stop = async (shareId: string) => {
    const { share } = await api.shares.stop(shareId);
    setShares((prev) => prev.map((s) => (s.shareId === shareId ? share : s)));
  };

  const recipient = members.find((m) => m.memberId === who);
  const message = link ? t('share.message', { child: childName, link, date: formatDate(new Date(Date.now() + days * 86_400_000).toISOString()) }) : '';
  const live = shares.filter((s) => s.state === 'active');

  return (
    <Sheet open={open} onClose={onClose} title={t('share.title')}>
      <p className="text-body text-ink-soft">{t('share.body', { child: childName })}</p>
      {error && (
        <div className="mt-3">
          <Alert tone="warn">{error}</Alert>
        </div>
      )}

      {!link ? (
        <div className="mt-5 grid gap-5">
          {members.length > 0 && (
            <fieldset>
              <legend className="mb-2 text-label text-ink">{t('share.who')}</legend>
              <div role="radiogroup" aria-label={t('share.who')} className="flex flex-wrap gap-2">
                <Chip role="radio" selected={!who} onToggle={() => setWho(undefined)}>
                  {t('share.anyone')}
                </Chip>
                {members.map((m) => (
                  <Chip key={m.memberId} role="radio" selected={who === m.memberId} onToggle={() => setWho(m.memberId)}>
                    {m.name}
                  </Chip>
                ))}
              </div>
            </fieldset>
          )}
          <fieldset>
            <legend className="mb-2 text-label text-ink">{t('share.for')}</legend>
            <div role="radiogroup" aria-label={t('share.for')} className="flex flex-wrap gap-2">
              {DAYS.map((d) => (
                <Chip key={d} role="radio" selected={days === d} onToggle={() => setDays(d)}>
                  {t(d === 1 ? 'share.day' : 'share.days', { n: d })}
                </Chip>
              ))}
            </div>
          </fieldset>
          <Button icon={<LinkIcon className="h-4 w-4" />} loading={busy} onClick={() => void create()}>
            {t('share.create')}
          </Button>
        </div>
      ) : (
        <div className="mt-5 grid gap-3">
          <p className="rounded-md bg-paper px-4 py-3 text-caption break-all text-ink">{link}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() =>
                void copyText(link).then((ok) => toast(ok ? t('share.copied') : t('common.error'), { tone: ok ? 'progress' : 'alert' }))
              }
            >
              {t('share.copy')}
            </Button>
            <Button as="a" href={whatsappHref(message)} target="_blank" rel="noopener noreferrer" variant="secondary" icon={<WhatsApp className="h-4 w-4" />}>
              WhatsApp
            </Button>
            <Button as="a" href={mailtoHref(t('share.subject', { child: childName }), message, recipient?.email ?? '')} variant="secondary" icon={<Mail className="h-4 w-4" />}>
              {t('share.email')}
            </Button>
          </div>
          <p className="text-caption text-ink-muted">{t('share.once')}</p>
        </div>
      )}

      {live.length > 0 && (
        <section aria-labelledby="live-links" className="mt-7 border-t border-line pt-5">
          <h3 id="live-links" className="text-label text-ink">{t('share.live')}</h3>
          <ul className="mt-2 grid gap-2">
            {live.map((s) => (
              <li key={s.shareId} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-paper px-4 py-2.5 text-caption">
                <span>
                  <span className="font-bold text-ink">{s.memberName ?? t('share.anyone')}</span>
                  <span className="text-ink-muted"> · {t('share.until', { date: formatDate(s.expiresAt) })} · {t('share.opens', { n: s.opens })}</span>
                </span>
                <button type="button" onClick={() => void stop(s.shareId)} className="min-h-tap px-2 font-bold text-alert-ink hover:underline">
                  {t('share.stop')}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Sheet>
  );
};
